import { Stack, Typography, Button } from '@mui/material';
import { Box } from '@mui/system';
import { showToast, SwipableDialog } from '@tix-ticketing/ui-components';
import { ArrowRightIcon } from '@tix-ticketing/ui-icons/outline';
import { FormattedMessage, useIntl } from 'react-intl';
import type { SpecialOfferResponse } from 'api/generated/types.gen';
import { EventGroupOfferCard } from 'components/common/cards/event-group-offer-card';
export const SpecialOffersDialog = ({
  open,
  onClose,
  onApplySpecialOffer,
  availableSpecialOffers,
  isApplying = false
}: {
  open: boolean;
  onClose: () => void;
  onApplySpecialOffer: (specialOfferId: number) => void;
  availableSpecialOffers: SpecialOfferResponse[];
  isApplying?: boolean;
}) => {
  const intl = useIntl();
  const handleApplySpecialOffer = (specialOfferId?: number) => {
    if (!specialOfferId) {
      showToast({
        title: intl.formatMessage({
          id: "offer.applyError.title",
          defaultMessage: [{
            "type": 0,
            "value": "Cannot apply special offer"
          }]
        }),
        message: intl.formatMessage({
          id: "offer.applyError.description",
          defaultMessage: [{
            "type": 0,
            "value": "Something went wrong when trying to apply the special offer"
          }]
        }),
        severity: 'error'
      });
      return;
    }
    onApplySpecialOffer(specialOfferId);
  };
  return <SwipableDialog open={open} onClose={onClose} headerText={intl.formatMessage({
    id: "offer.dialog.title",
    defaultMessage: [{
      "type": 6,
      "value": "numOffers",
      "options": {
        "=1": {
          "value": [{
            "type": 0,
            "value": "Special offer for you"
          }]
        },
        "other": {
          "value": [{
            "type": 0,
            "value": "Special offers for you"
          }]
        }
      },
      "offset": 0,
      "pluralType": "cardinal"
    }]
  }, {
    numOffers: availableSpecialOffers.length
  })} subtitleText={intl.formatMessage({
    id: "offer.dialog.subtitle",
    defaultMessage: [{
      "type": 6,
      "value": "numOffers",
      "options": {
        "=1": {
          "value": [{
            "type": 0,
            "value": "You have access to a special offer for these events. Click view dates to see the dates available for you to choose from."
          }]
        },
        "other": {
          "value": [{
            "type": 0,
            "value": "You have access to special offers for these events. Click the event to see the dates available for you to choose from."
          }]
        }
      },
      "offset": 0,
      "pluralType": "cardinal"
    }]
  }, {
    numOffers: availableSpecialOffers.length
  })} primaryActionButton={availableSpecialOffers.length === 1 && <Button fullWidth variant="contained" color="primary" size="large" endIcon={<ArrowRightIcon />} loading={isApplying} loadingPosition="end" onClick={() => handleApplySpecialOffer(availableSpecialOffers[0]?.specialOfferId)}>
            <FormattedMessage id="event.offers.viewDates" defaultMessage={[{
      "type": 0,
      "value": "View dates"
    }]} />
          </Button>} secondaryActionButton={<Button fullWidth variant="contained" color="monochrome" size="large" disabled={isApplying} onClick={() => onClose()}>
          <FormattedMessage id="event.offers.skip" defaultMessage={[{
      "type": 0,
      "value": "Skip"
    }]} />
        </Button>}>
      <Stack sx={{
      display: 'flex',
      gap: 3,
      alignItems: 'flex-start',
      width: '100%'
    }}>
        {availableSpecialOffers.map(offer => <OfferListItem key={offer.specialOfferId} offer={offer} onClick={availableSpecialOffers.length > 1 ? () => handleApplySpecialOffer(offer.specialOfferId) : undefined} />)}
      </Stack>
    </SwipableDialog>;
};
const OfferListItem = ({
  offer,
  onClick
}: {
  offer: SpecialOfferResponse;
  onClick?: () => void;
}) => {
  return <Box sx={{
    width: '100%',
    gap: 2,
    display: 'flex',
    flexDirection: 'column',
    py: 1
  }}>
      <Typography variant="body1Medium">{offer.name}</Typography>
      <EventGroupOfferCard data={offer} variant="compact" onClick={onClick ? () => onClick() : undefined} />
    </Box>;
};